// Shared logic for the registrations page and the entry editor: rounding
// preview (same rules as the server), default times, rate prefill, payment
// status cycling and undo for deletes.

import { api } from '../api';
import { data, type EntryInput } from '../stores/data.svelte';
import { DEFAULT_ROUNDING } from '../stores/account.svelte';
import { clientColor, entryAmount, hhmm, normalizeStatus } from '../format';
import type { Client, RoundingSettings, TimeEntry } from '../types';

export type Status = 'open' | 'partial' | 'paid' | 'non_billable';
export const STATUSES: Status[] = ['open', 'partial', 'paid', 'non_billable'];

// ---------- Rounding (mirrors computeRoundedDuration in server.js) ----------

/** Client rounding wins when the client has it switched on; otherwise the user's settings. */
export function effectiveRounding(client: Pick<Client, 'rounding_settings'> | null | undefined, user: Partial<RoundingSettings>): RoundingSettings {
  const own = client?.rounding_settings;
  const source = own && own.rounding_enabled === true ? own : user;
  return { ...DEFAULT_ROUNDING, ...(source || {}) };
}

export function roundingActive(s: Partial<RoundingSettings>): boolean {
  return !!s.rounding_enabled && !!s.rounding_mode && s.rounding_mode !== 'none';
}

export function roundMinutes(minutes: number, s: Partial<RoundingSettings>): { minutes: number; applied: boolean } {
  const original = Math.max(0, Math.round(Number(minutes) || 0));
  if (!roundingActive(s)) return { minutes: original, applied: false };
  const inc = Number(s.rounding_increment) >= 1 && Number(s.rounding_increment) <= 1440 ? Number(s.rounding_increment) : 15;
  const grace = Math.max(0, Number(s.grace_period) || 0);
  let working = original;
  if (grace > 0) {
    const rem = working % inc;
    if (rem > 0 && rem <= grace) working -= rem;
  }
  let rounded: number;
  switch (s.rounding_mode) {
    case 'up':
      rounded = Math.ceil(working / inc) * inc;
      break;
    case 'down':
      rounded = Math.floor(working / inc) * inc;
      break;
    case 'threshold': {
      const threshold = s.rounding_threshold ?? Math.ceil(inc / 2);
      const base = Math.floor(working / inc) * inc;
      rounded = working % inc >= threshold ? base + inc : base;
      break;
    }
    default:
      rounded = Math.round(working / inc) * inc;
  }
  rounded = Math.max(0, rounded);
  const min = Math.max(0, Number(s.min_billable_time) || 0);
  if (min && rounded < min) rounded = min;
  return { minutes: rounded, applied: true };
}

// ---------- Times ----------

function toMinutes(value: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(value || '');
  if (!m) return null;
  return +m[1] * 60 + +m[2];
}

function fromMinutes(total: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(total)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/** end − start in minutes (negative when the end is before the start), or null. */
export function spanMinutes(start: string, end: string): number | null {
  const a = toMinutes(start);
  const b = toMinutes(end);
  if (a === null || b === null) return null;
  return b - a;
}

/** 'HH:MM' part of 'YYYY-MM-DD HH:MM:SS'. */
export function timeOf(value: string | null | undefined): string {
  return (value || '').slice(11, 16);
}

export function entryDate(e: Pick<TimeEntry, 'date' | 'start_time'>): string {
  return e.date || (e.start_time || '').slice(0, 10);
}

/** Colour of the entry's client (automatic colour for unknown clients). */
export function entryColor(e: Pick<TimeEntry, 'client_id'>): string {
  return clientColor(e.client_id !== null ? data.clientById.get(e.client_id) ?? { id: e.client_id } : null);
}

/**
 * Default times for a new entry on `date`: start where the last entry of that
 * day ended (or 09:00), end = now rounded to 5 minutes (today) or start + 1 h.
 */
export function defaultTimes(date: string, entries: TimeEntry[], todayIso: string): { start: string; end: string } {
  let lastEnd: number | null = null;
  for (const e of entries) {
    if (entryDate(e) !== date) continue;
    const end = toMinutes(timeOf(e.end_time));
    if (end !== null && (lastEnd === null || end > lastEnd)) lastEnd = end;
  }
  const start = lastEnd ?? 9 * 60;
  let end: number;
  if (date === todayIso) {
    const now = new Date();
    end = Math.round((now.getHours() * 60 + now.getMinutes()) / 5) * 5;
    if (end <= start) end = start + 60;
  } else {
    end = start + 60;
  }
  if (end > 23 * 60 + 59) end = 23 * 60 + 59;
  return { start: fromMinutes(start), end: fromMinutes(end) };
}

export function nowRounded(): string {
  const d = new Date();
  const m = Math.round((d.getHours() * 60 + d.getMinutes()) / 5) * 5;
  return m >= 24 * 60 ? hhmm(d) : fromMinutes(m);
}

// ---------- Rate prefill (same order as the timer) ----------

export async function prefillRate(clientId: number): Promise<number | null> {
  const client = data.clientById.get(clientId);
  const own = Number(client?.default_rate);
  if (Number.isFinite(own) && own > 0) return own;
  try {
    const res = await api.get<{ rate: number | string | null }>(`/api/clients/${clientId}/last-rate`, { retries: 1 });
    const r = Number(res?.rate);
    if (Number.isFinite(r) && r > 0) return r;
  } catch { /* fall through */ }
  try {
    const res = await api.get<{ rate: number | string | null }>('/api/last-used-rate', { retries: 1 });
    const r = Number(res?.rate);
    if (Number.isFinite(r) && r > 0) return r;
  } catch { /* none */ }
  return null;
}

// ---------- Payment ----------

export const round2 = (n: number) => Math.round(n * 100) / 100;

/** Status badge click: open → partial (50 %) → paid → non-billable → open. */
export function nextStatus(status: string | null | undefined): Status {
  const s = normalizeStatus(status);
  return s === 'open' ? 'partial' : s === 'partial' ? 'paid' : s === 'paid' ? 'non_billable' : 'open';
}

export async function cycleStatus(entry: TimeEntry): Promise<Status> {
  const next = nextStatus(entry.payment_status);
  if (next === 'partial') {
    const total = entryAmount(entry);
    const half = round2(total * 0.5 - (Number(entry.amount_paid) || 0));
    if (half > 0) {
      await data.recordPayment([entry.id], 'partial', { amount: half, proportional: true });
      return 'partial';
    }
    await data.recordPayment([entry.id], 'paid');
    return 'paid';
  }
  await data.recordPayment([entry.id], next);
  return next;
}

// ---------- Undo ----------

/** Re-create deleted entries (new ids) including their payment state. */
export async function restoreEntries(entries: TimeEntry[]): Promise<void> {
  for (const e of entries) {
    const status = normalizeStatus(e.payment_status);
    const input: EntryInput = {
      date: entryDate(e),
      start_time: timeOf(e.start_time),
      end_time: timeOf(e.end_time),
      client_id: e.client_id,
      activity_id: e.activity_id,
      description: e.description,
      rate: e.rate,
      billable: status !== 'non_billable',
      pause_minutes: Number(e.pause_minutes) || 0,
      invoice_reference: e.invoice_reference
    };
    const created = await data.createEntry(input);
    const paid = Number(e.amount_paid) || 0;
    if (status === 'paid' || status === 'partial' || paid > 0) {
      await data.updateEntry(created.id, { payment_status: status, amount_paid: paid });
    }
  }
}
